import { ApiError, HttpClient, isTenantRestrictedModeActiveError } from './http-client.js';
import { extractRefreshToken, extractToken } from './parsers.js';
import { getRuntimeConfig } from '../runtime-config.js';

const DEFAULT_AUTH_CONFIG = {
  loginEndpoint: '/auth/login',
  refreshEndpoint: '/auth/refresh',
  currentUserEndpoint: '/auth/who',
  profilesEndpoint: '/admin/profiles',
  loginField: 'username',
  passwordField: 'password',
  loginSectionField: 'section',
  loginSectionValue: 'admin',
};

function buildRequestIdHeaders(requestId) {
  const normalizedRequestId = String(requestId || '').trim();
  return normalizedRequestId ? { 'X-Request-Id': normalizedRequestId } : undefined;
}

function normalizeCurrencyRatePair(baseCurrency, currency) {
  const normalizedBaseCurrency = String(baseCurrency || '').trim().toUpperCase();
  const normalizedCurrency = String(currency || '').trim().toUpperCase();

  if (!normalizedBaseCurrency || !normalizedCurrency) {
    throw new Error('Currency pair is required');
  }

  return {
    baseCurrency: normalizedBaseCurrency,
    currency: normalizedCurrency,
  };
}

export class AdminApi {
  constructor(options = {}) {
    this.client = new HttpClient({
      baseUrl: options.baseUrl,
      defaultHeaders: options.defaultHeaders,
    });

    this.endpoints = {
      login: options.loginEndpoint,
      refresh: options.refreshEndpoint,
      currentUser: options.currentUserEndpoint,
      profiles: options.profilesEndpoint,
    };

    this.authFields = {
      login: options.loginField,
      password: options.passwordField,
      sectionField: options.loginSectionField,
      sectionValue: options.loginSectionValue,
    };

    this.auth = {
      getAccessToken: null,
      getRefreshToken: null,
      getAuthFlowVersion: null,
      onAuthTokens: null,
      onAuthFailure: null,
    };

    this.refreshInFlight = null;
    this.pendingRequests = 0;
    this.dictionaryCache = new Map();
    this.dictionaryInFlight = new Map();
  }

  static fromEnv(env = process.env) {
    const runtimeConfig = getRuntimeConfig();

    return new AdminApi({
      baseUrl: (runtimeConfig.apiBaseUrl || env.REACT_APP_API_BASE_URL || 'http://localhost/api').replace(/\/$/, ''),
      loginEndpoint: DEFAULT_AUTH_CONFIG.loginEndpoint,
      refreshEndpoint: DEFAULT_AUTH_CONFIG.refreshEndpoint,
      currentUserEndpoint: DEFAULT_AUTH_CONFIG.currentUserEndpoint,
      profilesEndpoint: DEFAULT_AUTH_CONFIG.profilesEndpoint,
      loginField: DEFAULT_AUTH_CONFIG.loginField,
      passwordField: DEFAULT_AUTH_CONFIG.passwordField,
      loginSectionField: DEFAULT_AUTH_CONFIG.loginSectionField,
      loginSectionValue: DEFAULT_AUTH_CONFIG.loginSectionValue,
    });
  }

  configureRuntime(options = {}) {
    if (options.baseUrl) {
      this.client.baseUrl = String(options.baseUrl).replace(/\/$/, '');
    }
  }

  configureAuth(options = {}) {
    this.auth = {
      getAccessToken: options.getAccessToken || null,
      getRefreshToken: options.getRefreshToken || null,
      getAuthFlowVersion: options.getAuthFlowVersion || null,
      onAuthTokens: options.onAuthTokens || null,
      onAuthFailure: options.onAuthFailure || null,
    };
  }

  resolveAccessToken(explicitToken) {
    return explicitToken || (this.auth.getAccessToken ? this.auth.getAccessToken() : null);
  }

  resolveRefreshToken() {
    return this.auth.getRefreshToken ? this.auth.getRefreshToken() : null;
  }

  async refreshAccessToken(authFlowVersion) {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const currentRefreshToken = this.resolveRefreshToken();
    if (!currentRefreshToken) {
      throw new Error('Refresh token is missing');
    }
    const refreshAuthFlowVersion = authFlowVersion !== undefined
      ? authFlowVersion
      : this.auth.getAuthFlowVersion
        ? this.auth.getAuthFlowVersion()
        : undefined;

	    this.refreshInFlight = (async () => {
	      try {
	        const body = {
	          refresh_token: currentRefreshToken,
	        };
	        if (this.authFields.sectionField) {
	          body[this.authFields.sectionField] = this.authFields.sectionValue;
	        }

	        const response = await this.client.request(this.endpoints.refresh, {
	          method: 'POST',
	          body,
	        });

        const nextAccessToken = extractToken(response);
        const nextRefreshToken = extractRefreshToken(response) || currentRefreshToken;

        if (!nextAccessToken) {
          throw new Error('API did not return refreshed access token');
        }

        if (this.auth.onAuthTokens) {
          this.auth.onAuthTokens({
            token: nextAccessToken,
            refreshToken: nextRefreshToken,
            response,
            authFlowVersion: refreshAuthFlowVersion,
          });
        }

        return nextAccessToken;
      } catch (error) {
        if (this.auth.onAuthFailure) {
          this.auth.onAuthFailure(error, {
            authFlowVersion: refreshAuthFlowVersion,
          });
        }
        throw error;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  notifyLoadingChange() {
    if (typeof window === 'undefined') {
      return;
    }

    window.dispatchEvent(new CustomEvent('admin-api-loading', {
      detail: {
        isLoading: this.pendingRequests > 0,
        count: this.pendingRequests,
      },
    }));
  }

  beginRequest() {
    this.pendingRequests += 1;
    this.notifyLoadingChange();
  }

  endRequest() {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    this.notifyLoadingChange();
  }

  notifyRestrictedModeAuthFailure(error, authFlowVersion) {
    if (!isTenantRestrictedModeActiveError(error)) {
      return false;
    }

    if (this.auth.onAuthFailure) {
      this.auth.onAuthFailure(error, { authFlowVersion });
    }

    return true;
  }

  async requestWithAuth({ token, retryOnForbidden = true, ...request }) {
    const resolvedToken = this.resolveAccessToken(token);
    const requestAuthFlowVersion = this.auth.getAuthFlowVersion ? this.auth.getAuthFlowVersion() : undefined;
    const mergedHeaders = {
      ...(request.headers || {}),
    };

    if (resolvedToken) {
      mergedHeaders.Authorization = `Bearer ${resolvedToken}`;
    }

    this.beginRequest();
    try {
      return await this.client.request(request.path, {
        method: request.method || 'GET',
        query: request.query,
        body: request.body,
        headers: mergedHeaders,
      });
    } catch (error) {
      const errorMessage = String(error?.message || '').toLowerCase();
      const isExpiredJwt401 =
        error?.status === 401 &&
        (errorMessage.includes('expired jwt token') || errorMessage.includes('jwt token expired'));
      const shouldRefresh =
        retryOnForbidden &&
        isExpiredJwt401 &&
        this.resolveRefreshToken();
      if (!shouldRefresh) {
        this.notifyRestrictedModeAuthFailure(error, requestAuthFlowVersion);
        throw error;
      }

      const refreshedToken = await this.refreshAccessToken(requestAuthFlowVersion);
      return this.requestWithAuth({
        ...request,
        token: refreshedToken,
        retryOnForbidden: false,
      });
    } finally {
      this.endRequest();
    }
  }

  async requestBlobWithAuth({ token, retryOnForbidden = true, ...request }) {
    const resolvedToken = this.resolveAccessToken(token);
    const requestAuthFlowVersion = this.auth.getAuthFlowVersion ? this.auth.getAuthFlowVersion() : undefined;
    const mergedHeaders = {
      ...(request.headers || {}),
    };

    if (resolvedToken) {
      mergedHeaders.Authorization = `Bearer ${resolvedToken}`;
    }

    this.beginRequest();
    try {
      const response = await fetch(this.client.resolveUrl(request.path), {
        method: request.method || 'GET',
        headers: mergedHeaders,
        body: request.body,
      });

      if (!response.ok) {
        const rawBody = await response.text();
        let parsedBody = null;
        if (rawBody) {
          try {
            parsedBody = JSON.parse(rawBody);
          } catch {
            parsedBody = rawBody;
          }
        }

        const errorMessage = String(parsedBody?.message || parsedBody?.error || parsedBody?.detail || '').toLowerCase();
        const isExpiredJwt401 =
          response.status === 401 &&
          (errorMessage.includes('expired jwt token') || errorMessage.includes('jwt token expired'));
        const shouldRefresh =
          retryOnForbidden &&
          isExpiredJwt401 &&
          this.resolveRefreshToken();
        if (shouldRefresh) {
          const refreshedToken = await this.refreshAccessToken(requestAuthFlowVersion);
          return this.requestBlobWithAuth({
            ...request,
            token: refreshedToken,
            retryOnForbidden: false,
          });
        }

        const { message, errors } = HttpClient.parseApiErrors(parsedBody);
        const error = new ApiError(message || `Request failed with status ${response.status}`, {
          status: response.status,
          body: parsedBody,
          errors,
        });
        this.notifyRestrictedModeAuthFailure(error, requestAuthFlowVersion);

        throw error;
      }

      const disposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);

      return {
        blob: await response.blob(),
        filename: filenameMatch?.[1] || request.fallbackFilename || 'backup.sql.gz',
      };
    } finally {
      this.endRequest();
    }
  }

  async requestDictionary(path, token, { forceRefresh = false } = {}) {
    const key = String(path || '').trim();
    if (!key) {
      throw new Error('Dictionary path is required');
    }

    if (!forceRefresh && this.dictionaryCache.has(key)) {
      return this.dictionaryCache.get(key);
    }

    if (!forceRefresh && this.dictionaryInFlight.has(key)) {
      return this.dictionaryInFlight.get(key);
    }

    const inFlight = this.requestWithAuth({
      token,
      method: 'GET',
      path: key,
    })
      .then((response) => {
        this.dictionaryCache.set(key, response);
        return response;
      })
      .finally(() => {
        this.dictionaryInFlight.delete(key);
      });

    this.dictionaryInFlight.set(key, inFlight);
    return inFlight;
  }

  clearDictionaryCache() {
    this.dictionaryCache.clear();
    this.dictionaryInFlight.clear();
  }

  async login(identifier, password) {
    const payload = {
      [this.authFields.login]: identifier,
      [this.authFields.password]: password,
    };

    if (this.authFields.sectionField) {
      payload[this.authFields.sectionField] = this.authFields.sectionValue;
    }

    return this.client.request(this.endpoints.login, {
      method: 'POST',
      body: payload,
    });
  }

  async verifyTwoFactor(challengeId, code) {
    return this.client.request('/auth/2fa/verify', {
      method: 'POST',
      body: {
        challengeId,
        code,
      },
    });
  }

  async getTwoFactorStatus(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/auth/2fa/status',
    });
  }

  async setupTwoFactor(token) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/auth/2fa/setup',
    });
  }

  async confirmTwoFactor(token, code) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/auth/2fa/confirm',
      body: { code },
    });
  }

  async disableTwoFactor(token, password, code) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/auth/2fa/disable',
      body: { password, code },
    });
  }

  async regenerateTwoFactorRecoveryCodes(token, password, code) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/auth/2fa/recovery-codes/regenerate',
      body: { password, code },
    });
  }

	  async getCurrentUser(token, { retryOnForbidden = true } = {}) {
	    try {
	      return await this.requestWithAuth({
	        token,
	        retryOnForbidden,
	        method: 'GET',
	        path: this.endpoints.currentUser,
	        query: this.authFields.sectionField
	          ? { [this.authFields.sectionField]: this.authFields.sectionValue }
	          : undefined,
	      });
	    } catch (error) {
	      if ((error?.status === 401 || error?.status === 403) && this.auth.onAuthFailure) {
	        this.auth.onAuthFailure(error, {
	          authFlowVersion: this.auth.getAuthFlowVersion ? this.auth.getAuthFlowVersion() : undefined,
	        });
	      }

	      throw error;
	    }
	  }

  async getProfiles(token, filters = {}) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: this.endpoints.profiles,
      body: filters,
    });
  }

  async getProfile(token, id) {
    const profileId = String(id || '').trim();
    if (!profileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/profile/${encodeURIComponent(profileId)}`,
    });
  }

  async getLeads(token, filters = {}) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/lead',
      body: filters,
    });
  }

  async getLead(token, id) {
    const leadId = String(id || '').trim();
    if (!leadId) {
      throw new Error('Lead id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/lead/${encodeURIComponent(leadId)}`,
    });
  }

  async getLeadDetails(token, id) {
    const leadId = String(id || '').trim();
    if (!leadId) {
      throw new Error('Lead id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/lead/${encodeURIComponent(leadId)}/details`,
    });
  }

  async createLead(token, payload = {}) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/lead/create',
      body: payload,
    });
  }

  async uploadLeadImport(token, file, encoding = 'auto') {
    const body = new FormData();
    body.append('file', file);
    body.append('encoding', encoding);
    return this.requestWithAuth({ token, method: 'POST', path: '/admin/lead/imports', body });
  }

  async listLeadImports(token, { offset = 0, limit = 25 } = {}) {
    return this.requestWithAuth({ token, method: 'GET', path: `/admin/lead/imports?offset=${offset}&limit=${limit}` });
  }

  async getLeadImport(token, uid, { offset = 0, limit = 100 } = {}) {
    return this.requestWithAuth({ token, method: 'GET', path: `/admin/lead/imports/${encodeURIComponent(uid)}?offset=${offset}&limit=${limit}` });
  }

  async previewLeadImport(token, uid, body) {
    return this.requestWithAuth({ token, method: 'POST', path: `/admin/lead/imports/${encodeURIComponent(uid)}/preview`, body });
  }

  async confirmLeadImport(token, uid, revision) {
    return this.requestWithAuth({ token, method: 'POST', path: `/admin/lead/imports/${encodeURIComponent(uid)}/confirm`, body: { revision } });
  }

  async deleteLeadImport(token, uid) {
    return this.requestWithAuth({ token, method: 'DELETE', path: `/admin/lead/imports/${encodeURIComponent(uid)}` });
  }

  async downloadLeadImportReport(token, uid) {
    return this.requestBlobWithAuth({ token, method: 'GET', path: `/admin/lead/imports/${encodeURIComponent(uid)}/report`, fallbackFilename: 'lead-import-results.csv' });
  }

  async updateLead(token, id, payload = {}) {
    const leadId = String(id || '').trim();
    if (!leadId) {
      throw new Error('Lead id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/lead/${encodeURIComponent(leadId)}`,
      body: payload,
    });
  }

  async assignLead(token, id, managerId) {
    const leadId = String(id || '').trim();
    const normalizedManagerId = String(managerId || '').trim();
    if (!leadId) {
      throw new Error('Lead id is required');
    }
    if (!normalizedManagerId) {
      throw new Error('Manager id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/lead/${encodeURIComponent(leadId)}/assign`,
      body: {
        managerId: normalizedManagerId,
      },
    });
  }

  async unassignLead(token, id) {
    const leadId = String(id || '').trim();
    if (!leadId) {
      throw new Error('Lead id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/lead/${encodeURIComponent(leadId)}/unassign`,
    });
  }

  async deleteLead(token, id, payload = {}) {
    const leadId = String(id || '').trim();
    if (!leadId) {
      throw new Error('Lead id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'DELETE',
      path: `/admin/lead/${encodeURIComponent(leadId)}`,
      body: {
        reason: String(payload.reason || '').trim(),
      },
    });
  }

  async updateProfile(token, id, payload) {
    const profileId = String(id || '').trim();
    if (!profileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/profile/${encodeURIComponent(profileId)}`,
      body: payload,
    });
  }

  async updateProfileEmail(token, id, payload) {
    const profileId = String(id || '').trim();
    if (!profileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/profile/${encodeURIComponent(profileId)}/email`,
      body: payload,
    });
  }

  async revealProfileEmail(token, id, payload, options = {}) {
    const profileId = String(id || '').trim();
    if (!profileId) {
      throw new Error('Profile id is required');
    }

    const headers = buildRequestIdHeaders(options.requestId);
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/profile/${encodeURIComponent(profileId)}/pii/email/reveal`,
      body: payload,
      ...(headers ? { headers } : {}),
    });
  }

  async revealProfilePhone(token, id, payload, options = {}) {
    const profileId = String(id || '').trim();
    if (!profileId) {
      throw new Error('Profile id is required');
    }

    const headers = buildRequestIdHeaders(options.requestId);
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/profile/${encodeURIComponent(profileId)}/pii/phone/reveal`,
      body: payload,
      ...(headers ? { headers } : {}),
    });
  }

  async anonymizeProfile(token, id) {
    const profileId = String(id || '').trim();
    if (!profileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/profile/${encodeURIComponent(profileId)}/anonymize`,
    });
  }

  async exportProfile(token, id, options = {}) {
    const profileId = String(id || '').trim();
    if (!profileId) {
      throw new Error('Profile id is required');
    }

    const headers = buildRequestIdHeaders(options.requestId);
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/profile/${encodeURIComponent(profileId)}/export`,
      ...(headers ? { headers } : {}),
    });
  }

  async verifySecurityStepUp(token, payloadOrCode, options = {}) {
    const payload = typeof payloadOrCode === 'string'
      ? { code: payloadOrCode }
      : { ...(payloadOrCode || {}) };
    const headers = buildRequestIdHeaders(options.requestId);

    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/security/step-up/verify',
      body: payload,
      ...(headers ? { headers } : {}),
    });
  }

  async getProfileActionLog(token, profileId, query = {}) {
    const normalizedProfileId = String(profileId || '').trim();
    if (!normalizedProfileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/action-log',
      query: {
        profileId: normalizedProfileId,
        ...query,
      },
    });
  }

  async searchActionLogs(token, query = {}) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/action-log/search',
      query,
    });
  }

  async updateProfileStatus(token, id, status) {
    const profileId = String(id || '').trim();
    if (!profileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/profile/${encodeURIComponent(profileId)}/status`,
      body: {
        status,
      },
    });
  }

  async invalidateProfile(token, id, payload = {}) {
    const profileId = String(id || '').trim();
    if (!profileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/profile/${encodeURIComponent(profileId)}/invalidate`,
      body: {
        reason: String(payload.reason || '').trim(),
      },
    });
  }

  async validateProfile(token, id, payload = {}) {
    const profileId = String(id || '').trim();
    if (!profileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/profile/${encodeURIComponent(profileId)}/validate`,
      body: {
        reason: String(payload.reason || '').trim(),
      },
    });
  }

  async assignProfile(token, id, managerId) {
    const profileId = String(id || '').trim();
    const normalizedManagerId = String(managerId || '').trim();
    if (!profileId) {
      throw new Error('Profile id is required');
    }
    if (!normalizedManagerId) {
      throw new Error('Manager id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/profile/${encodeURIComponent(profileId)}/assign`,
      body: {
        managerId: normalizedManagerId,
      },
    });
  }

  async unassignProfile(token, id) {
    const profileId = String(id || '').trim();
    if (!profileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/profile/${encodeURIComponent(profileId)}/unassign`,
    });
  }

  async getAccounts(token, filters = {}) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/accounts',
      body: filters,
    });
  }

  async getProfileAccounts(token, profileId) {
    const normalizedProfileId = String(profileId || '').trim();
    if (!normalizedProfileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/accounts/${encodeURIComponent(normalizedProfileId)}`,
    });
  }

  async getProfilePartnership(token, profileUid) {
    const normalizedProfileUid = String(profileUid || '').trim();
    if (!normalizedProfileUid) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/profile/${encodeURIComponent(normalizedProfileUid)}/partnership`,
    });
  }

  async searchPartners(token, query, options = {}) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/partner/search',
      query: {
        query,
        limit: options.limit ?? 10,
      },
    });
  }

  async updateAccountPartner(token, accountUid, payload) {
    const normalizedAccountUid = String(accountUid || '').trim();
    if (!normalizedAccountUid) {
      throw new Error('Account id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/account/${encodeURIComponent(normalizedAccountUid)}/partner`,
      body: payload,
    });
  }

  async getProfileKyc(token, profileId) {
    const normalizedProfileId = String(profileId || '').trim();
    if (!normalizedProfileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/profile/${encodeURIComponent(normalizedProfileId)}/kyc`,
    });
  }

  async getProfileDocuments(token, profileId, query = {}) {
    const normalizedProfileId = String(profileId || '').trim();
    if (!normalizedProfileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/documents/client/${encodeURIComponent(normalizedProfileId)}`,
      query,
    });
  }

  async getProfileAuditIntegrity(token, profileId) {
    const normalizedProfileId = String(profileId || '').trim();
    if (!normalizedProfileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/profile/${encodeURIComponent(normalizedProfileId)}/audit-integrity`,
    });
  }

  async getProfileSuitability(token, profileId) {
    const normalizedProfileId = String(profileId || '').trim();
    if (!normalizedProfileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/profile/${encodeURIComponent(normalizedProfileId)}/suitability`,
    });
  }

  async createProfileSuitability(token, profileId, payload) {
    const normalizedProfileId = String(profileId || '').trim();
    if (!normalizedProfileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/profile/${encodeURIComponent(normalizedProfileId)}/suitability`,
      body: payload,
    });
  }

  async updateProfileVulnerability(token, profileId, payload) {
    const normalizedProfileId = String(profileId || '').trim();
    if (!normalizedProfileId) {
      throw new Error('Profile id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/profile/${encodeURIComponent(normalizedProfileId)}/vulnerability`,
      body: payload,
    });
  }

  async getDocuments(token, payload = {}) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/documents/client',
      body: payload,
    });
  }

  async getTransactions(token, payload = {}) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/transaction',
      body: payload,
    });
  }

  async getTransaction(token, id) {
    const transactionId = String(id || '').trim();
    if (!transactionId) {
      throw new Error('Transaction id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/transaction/${encodeURIComponent(transactionId)}`,
    });
  }

  async refreshPaymentExecution(token, id) {
    const transactionId = String(id || '').trim();
    if (!transactionId) throw new Error('Transaction id is required');
    return this.requestWithAuth({token, method: 'POST', path: `/admin/transaction/${encodeURIComponent(transactionId)}/payment/refresh`});
  }

  async recordPaymentEvidence(token, id, payload) {
    const transactionId = String(id || '').trim();
    if (!transactionId) throw new Error('Transaction id is required');
    return this.requestWithAuth({token, method: 'POST', path: `/admin/transaction/${encodeURIComponent(transactionId)}/payment/evidence`, body: payload});
  }

  async approveTransaction(token, id) {
    const transactionId = String(id || '').trim();
    if (!transactionId) {
      throw new Error('Transaction id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/transaction/${encodeURIComponent(transactionId)}/approve`,
    });
  }

  async declineTransaction(token, id) {
    const transactionId = String(id || '').trim();
    if (!transactionId) {
      throw new Error('Transaction id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/transaction/${encodeURIComponent(transactionId)}/decline`,
    });
  }

  async processOwnTransferNow(token, id) {
    const transactionId = String(id || '').trim();
    if (!transactionId) {
      throw new Error('Transaction id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/transaction/${encodeURIComponent(transactionId)}/own-transfer/process-now`,
    });
  }

  async completeOwnTransferManually(token, id, payload = {}) {
    const transactionId = String(id || '').trim();
    if (!transactionId) {
      throw new Error('Transaction id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/transaction/${encodeURIComponent(transactionId)}/own-transfer/manual-complete`,
      body: payload,
    });
  }

  async assignDocument(token, id, managerId) {
    const documentId = String(id || '').trim();
    const normalizedManagerId = String(managerId || '').trim();
    if (!documentId) {
      throw new Error('Document id is required');
    }
    if (!normalizedManagerId) {
      throw new Error('Manager id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/document/client/${encodeURIComponent(documentId)}/assign`,
      body: {
        managerId: normalizedManagerId,
      },
    });
  }

  async unassignDocument(token, id) {
    const documentId = String(id || '').trim();
    if (!documentId) {
      throw new Error('Document id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/document/client/${encodeURIComponent(documentId)}/unassign`,
    });
  }

  async syncAccount(token, id) {
    const accountId = String(id || '').trim();
    if (!accountId) {
      throw new Error('Account id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/account/${encodeURIComponent(accountId)}/sync`,
    });
  }

  async getAccountDetail(token, id) {
    const accountId = String(id || '').trim();
    if (!accountId) {
      throw new Error('Account id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/account/${encodeURIComponent(accountId)}`,
    });
  }

  async getProfileTrades(token, profileId, filters = {}) {
    const normalizedId = String(profileId || '').trim();
    if (!normalizedId) {
      throw new Error('Profile id is required');
    }

    const params = new URLSearchParams();
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.offset) params.set('offset', String(filters.offset));
    if (filters.symbol) params.set('symbol', filters.symbol);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);

    const query = params.toString();
    const path = `/admin/profiles/${encodeURIComponent(normalizedId)}/trades${query ? '?' + query : ''}`;

    return this.requestWithAuth({
      token,
      method: 'GET',
      path,
    });
  }

  async getAccountTrades(token, accountId, filters = {}) {
    const normalizedId = String(accountId || '').trim();
    if (!normalizedId) {
      throw new Error('Account id is required');
    }

    const params = new URLSearchParams();
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.offset) params.set('offset', String(filters.offset));
    if (filters.symbol) params.set('symbol', filters.symbol);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);

    const query = params.toString();
    const path = `/admin/accounts/${encodeURIComponent(normalizedId)}/trades${query ? '?' + query : ''}`;

    return this.requestWithAuth({
      token,
      method: 'GET',
      path,
    });
  }

  async getSystemUsers(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/system/users',
    });
  }

  async createSystemUser(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/system/users',
      body: payload,
    });
  }

  async updateSystemUser(token, id, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/system/users/${id}`,
      body: payload,
    });
  }

  async getSupportAccessGrants(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/system/support-access',
    });
  }

  async createSupportAccessGrant(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/system/support-access',
      body: payload,
    });
  }

  async revokeSupportAccessGrant(token, id, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/system/support-access/${id}/revoke`,
      body: payload,
    });
  }

  async extendSupportAccessGrant(token, id, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/system/support-access/${id}/extend`,
      body: payload,
    });
  }

  async regenerateSupportAccessInvite(token, id, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/system/support-access/${id}/invite/regenerate`,
      body: payload,
    });
  }

  async consumeSupportAccessInvite(tokenValue) {
    return this.client.request('/admin/support-access/consume', {
      method: 'POST',
      referrerPolicy: 'no-referrer',
      body: {
        token: tokenValue,
      },
    });
  }

  async getAclPermissions(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/acl/permissions',
    });
  }

  async getAclRoles(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/acl/roles',
    });
  }

  async createAclRole(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/acl/roles',
      body: payload,
    });
  }

  async updateAclRole(token, id, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/acl/roles/${encodeURIComponent(String(id || '').trim())}`,
      body: payload,
    });
  }

  async deleteAclRole(token, id) {
    return this.requestWithAuth({
      token,
      method: 'DELETE',
      path: `/admin/acl/roles/${encodeURIComponent(String(id || '').trim())}`,
    });
  }

  async getProfileRoles(token, profileId) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/profile/${encodeURIComponent(String(profileId || '').trim())}/roles`,
    });
  }

  async updateProfileRoles(token, profileId, roleIds) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/profile/${encodeURIComponent(String(profileId || '').trim())}/roles`,
      body: {
        roleIds,
      },
    });
  }

  async getJurisdictions(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/system/user-settings/jurisdictions',
    });
  }

  async saveJurisdiction(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/system/user-settings/jurisdictions',
      body: payload,
    });
  }

  async updateJurisdiction(token, id, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/system/user-settings/jurisdictions/${encodeURIComponent(String(id || '').trim())}`,
      body: payload,
    });
  }

  async deleteJurisdiction(token, id) {
    return this.requestWithAuth({
      token,
      method: 'DELETE',
      path: `/admin/system/user-settings/jurisdictions/${id}`,
    });
  }

  async getCountryDictionary(token, options = {}) {
    return this.requestDictionary('/dictionary/countries', token, options);
  }

  async getPlatformsDictionary(token, options = {}) {
    return this.requestDictionary('/dictionary/platforms', token, options);
  }

  async getCurrenciesDictionary(token, options = {}) {
    return this.requestDictionary('/dictionary/currencies', token, options);
  }

  async getPlaceholdersDictionary(token, options = {}) {
    return this.requestDictionary('/dictionary/placeholders', token, options);
  }

  async getTradingServers(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/system/user-settings/trading-servers',
    });
  }

  async getTradingPlatformAliases(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/system/user-settings/trading-platforms',
    });
  }

  async getEmailCredentials(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/system/settings/email',
    });
  }

  async getPaymentSettingsProviders(token) {
    return this.requestWithAuth({ token, method: 'GET', path: '/admin/payment-settings/providers' });
  }

  async getPaymentSettingsProvider(token, providerId) {
    return this.requestWithAuth({ token, method: 'GET', path: `/admin/payment-settings/providers/${encodeURIComponent(providerId)}` });
  }

  async updatePaymentSettingsProvider(token, providerId, payload) {
    return this.requestWithAuth({ token, method: 'PUT', path: `/admin/payment-settings/providers/${encodeURIComponent(providerId)}`, body: payload });
  }

  async getBankCredentials(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/payment-settings/bank-transfer',
    });
  }

  async updateBankCredentials(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: '/admin/payment-settings/bank-transfer',
      body: payload,
    });
  }

  async getCryptoCredentials(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/payment-settings/crypto-transfer',
    });
  }

  async updateCryptoCredentials(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: '/admin/payment-settings/crypto-transfer',
      body: payload,
    });
  }

  async getCurrencyRates(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/payment-settings/currency-rates',
    });
  }

  async reloadCurrencyRates(token) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/payment-settings/currency-rates/reload',
    });
  }

  async createCurrencyRate(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/payment-settings/currency-rates',
      body: payload,
    });
  }

  async updateCurrencyRateOverride(token, baseCurrency, currency, payload) {
    const pair = normalizeCurrencyRatePair(baseCurrency, currency);

    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/payment-settings/currency-rates/${encodeURIComponent(pair.baseCurrency)}/${encodeURIComponent(pair.currency)}/override`,
      body: payload,
    });
  }

  async resetCurrencyRateOverride(token, baseCurrency, currency, payload) {
    const pair = normalizeCurrencyRatePair(baseCurrency, currency);

    return this.requestWithAuth({
      token,
      method: 'DELETE',
      path: `/admin/payment-settings/currency-rates/${encodeURIComponent(pair.baseCurrency)}/${encodeURIComponent(pair.currency)}/override`,
      body: payload,
    });
  }

  async getCurrencyRateHistory(token, baseCurrency, currency, query = {}) {
    const pair = normalizeCurrencyRatePair(baseCurrency, currency);

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/payment-settings/currency-rates/${encodeURIComponent(pair.baseCurrency)}/${encodeURIComponent(pair.currency)}/history`,
      query,
    });
  }

  async updateEmailCredentials(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: '/admin/system/settings/email',
      body: payload,
    });
  }

  async getInternalTransferSettings(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/system/transaction-settings/internal-transfer',
    });
  }

  async getPaymentExecutionSettings(token) {
    return this.requestWithAuth({ token, method: 'GET', path: '/admin/system/transaction-settings/payment-execution' });
  }

  async getVerificationSettings(token) {
    return this.requestWithAuth({ token, method: 'GET', path: '/admin/system/verification-settings' });
  }

  async getProfileVerificationAccess(token, profileId) {
    return this.requestWithAuth({ token, method: 'GET', path: `/admin/profile/${encodeURIComponent(profileId)}/verification-access` });
  }

  async updateVerificationSettings(token, payload) {
    return this.requestWithAuth({ token, method: 'POST', path: '/admin/system/verification-settings', body: payload });
  }

  async updatePaymentExecutionSettings(token, payload) {
    return this.requestWithAuth({ token, method: 'PUT', path: '/admin/system/transaction-settings/payment-execution', body: payload });
  }

  async updateInternalTransferSettings(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: '/admin/system/transaction-settings/internal-transfer',
      body: payload,
    });
  }

  async getSystemGeneralSettings(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/system/settings/general',
    });
  }

  async updateSystemGeneralSettings(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: '/admin/system/settings/general',
      body: payload,
    });
  }

  async uploadSystemBrandingLogo(token, file) {
    const formData = new FormData();
    formData.append('logo', file);

    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/system/settings/branding/logo',
      body: formData,
    });
  }

  async deleteSystemBrandingLogo(token) {
    return this.requestWithAuth({
      token,
      method: 'DELETE',
      path: '/admin/system/settings/branding/logo',
    });
  }

  async listLegalDocuments(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/documents/legal',
    });
  }

  async uploadLegalDocument(token, formData) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/document/legal/upload',
      body: formData,
    });
  }

  async downloadLegalDocument(token, uid) {
    const normalizedUid = String(uid || '').trim();
    if (!normalizedUid) {
      throw new Error('Legal document uid is required');
    }

    return this.requestBlobWithAuth({
      token,
      method: 'GET',
      path: `/admin/document/legal/download/${encodeURIComponent(normalizedUid)}`,
      fallbackFilename: 'legal-document',
    });
  }

  async listLegalContentPages(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/legal/content',
    });
  }

  async getLegalContentPage(token, key) {
    const normalizedKey = String(key || '').trim();
    if (!normalizedKey) {
      throw new Error('Legal content page key is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/legal/content/${encodeURIComponent(normalizedKey)}`,
    });
  }

  async updateLegalContentPage(token, key, payload) {
    const normalizedKey = String(key || '').trim();
    if (!normalizedKey) {
      throw new Error('Legal content page key is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/legal/content/${encodeURIComponent(normalizedKey)}`,
      body: payload,
    });
  }

  async getPartnerSettings(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/partner/settings',
    });
  }

  async updatePartnerSettings(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: '/admin/partner/settings',
      body: payload,
    });
  }

  async getTaskSchedules(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/system/settings/tasks',
    });
  }

  async updateTaskSchedule(token, task, payload) {
    const normalizedTask = String(task || '').trim();
    if (!normalizedTask) {
      throw new Error('Task is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/system/settings/tasks/${encodeURIComponent(normalizedTask)}`,
      body: payload,
    });
  }

  async updateTaskScheduleStatus(token, task, payload) {
    const normalizedTask = String(task || '').trim();
    if (!normalizedTask) {
      throw new Error('Task is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/system/settings/tasks/${encodeURIComponent(normalizedTask)}/status`,
      body: payload,
    });
  }

  async updateTradingPlatformAliases(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: '/admin/system/user-settings/trading-platforms',
      body: payload,
    });
  }

  async createTradingServer(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/system/user-settings/trading-servers',
      body: payload,
    });
  }

  async updateTradingServer(token, id, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/system/user-settings/trading-servers/${encodeURIComponent(String(id || '').trim())}`,
      body: payload,
    });
  }

  async deleteTradingServer(token, id) {
    return this.requestWithAuth({
      token,
      method: 'DELETE',
      path: `/admin/system/user-settings/trading-servers/${encodeURIComponent(String(id || '').trim())}`,
    });
  }

  async getTradingAccounts(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/system/user-settings/trading-accounts',
    });
  }

  async createTradingAccount(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/system/user-settings/trading-accounts',
      body: payload,
    });
  }

  async updateTradingAccount(token, id, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/system/user-settings/trading-accounts/${encodeURIComponent(String(id || '').trim())}`,
      body: payload,
    });
  }

  async deleteTradingAccount(token, id) {
    return this.requestWithAuth({
      token,
      method: 'DELETE',
      path: `/admin/system/user-settings/trading-accounts/${encodeURIComponent(String(id || '').trim())}`,
    });
  }

  async getKycSettings(token, jurisdictionId) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}`,
    });
  }

  async getKycSteps(token, jurisdictionId) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step`,
    });
  }

  async getKycStep(token, jurisdictionId, stepId) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step/${encodeURIComponent(String(stepId || '').trim())}`,
    });
  }

  async getKycFields(token, jurisdictionId, stepId) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step/${encodeURIComponent(String(stepId || '').trim())}/field`,
    });
  }

  async getKycField(token, jurisdictionId, stepId, fieldId) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step/${encodeURIComponent(String(stepId || '').trim())}/field/${encodeURIComponent(String(fieldId || '').trim())}`,
    });
  }

  async getKycFieldDependencies(token, jurisdictionId, stepId, fieldId) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step/${encodeURIComponent(String(stepId || '').trim())}/field/${encodeURIComponent(String(fieldId || '').trim())}/dependency`,
    });
  }

  async getKycFieldDependency(token, jurisdictionId, stepId, fieldId, id) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step/${encodeURIComponent(String(stepId || '').trim())}/field/${encodeURIComponent(String(fieldId || '').trim())}/dependency/${encodeURIComponent(String(id || '').trim())}`,
    });
  }

  async createKycStep(token, jurisdictionId, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step`,
      body: payload,
    });
  }

  async updateKycStep(token, jurisdictionId, id, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step/${encodeURIComponent(String(id || '').trim())}`,
      body: payload,
    });
  }

  async deleteKycStep(token, jurisdictionId, id) {
    return this.requestWithAuth({
      token,
      method: 'DELETE',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step/${encodeURIComponent(String(id || '').trim())}`,
    });
  }

  async createKycField(token, jurisdictionId, stepId, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step/${encodeURIComponent(String(stepId || '').trim())}/field`,
      body: payload,
    });
  }

  async updateKycField(token, jurisdictionId, stepId, id, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step/${encodeURIComponent(String(stepId || '').trim())}/field/${encodeURIComponent(String(id || '').trim())}`,
      body: payload,
    });
  }

  async deleteKycField(token, jurisdictionId, stepId, id) {
    return this.requestWithAuth({
      token,
      method: 'DELETE',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step/${encodeURIComponent(String(stepId || '').trim())}/field/${encodeURIComponent(String(id || '').trim())}`,
    });
  }

  async createKycDependency(token, jurisdictionId, stepId, fieldId, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step/${encodeURIComponent(String(stepId || '').trim())}/field/${encodeURIComponent(String(fieldId || '').trim())}/dependency`,
      body: payload,
    });
  }

  async updateKycDependency(token, jurisdictionId, stepId, fieldId, id, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step/${encodeURIComponent(String(stepId || '').trim())}/field/${encodeURIComponent(String(fieldId || '').trim())}/dependency/${encodeURIComponent(String(id || '').trim())}`,
      body: payload,
    });
  }

  async deleteKycDependency(token, jurisdictionId, stepId, fieldId, id) {
    return this.requestWithAuth({
      token,
      method: 'DELETE',
      path: `/admin/system/user-settings/kyc/${encodeURIComponent(String(jurisdictionId || '').trim())}/step/${encodeURIComponent(String(stepId || '').trim())}/field/${encodeURIComponent(String(fieldId || '').trim())}/dependency/${encodeURIComponent(String(id || '').trim())}`,
    });
  }

  async requestAdmin({ token, method = 'GET', path, query, body, headers }) {
    return this.requestWithAuth({
      token,
      method,
      path,
      query,
      body,
      headers,
    });
  }

  async getSarQueue(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/sar',
    });
  }

  async createSar(token, profileUid, payload = {}) {
    const uid = String(profileUid || '').trim();
    if (!uid) {
      throw new Error('Profile UID is required');
    }

    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/profile/${encodeURIComponent(uid)}/sar`,
      body: payload,
    });
  }

  async completeSar(token, sarUid, payload = {}) {
    const uid = String(sarUid || '').trim();
    if (!uid) {
      throw new Error('SAR UID is required');
    }

    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/sar/${encodeURIComponent(uid)}/complete`,
      body: payload,
    });
  }

  async getComplaintReport(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/complaint/report',
    });
  }

  async createComplaint(token, payload = {}) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/complaint',
      body: payload,
    });
  }

  async acknowledgeComplaint(token, complaintUid) {
    const uid = String(complaintUid || '').trim();
    if (!uid) {
      throw new Error('Complaint UID is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/complaint/${encodeURIComponent(uid)}/acknowledge`,
    });
  }

  async sendComplaintHoldingResponse(token, complaintUid) {
    const uid = String(complaintUid || '').trim();
    if (!uid) {
      throw new Error('Complaint UID is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/complaint/${encodeURIComponent(uid)}/holding-response`,
    });
  }

  async resolveComplaint(token, complaintUid, payload = {}) {
    const uid = String(complaintUid || '').trim();
    if (!uid) {
      throw new Error('Complaint UID is required');
    }

    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/complaint/${encodeURIComponent(uid)}/resolve`,
      body: payload,
    });
  }

  async getBreachIncidents(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/breach',
    });
  }

  async createBreachIncident(token, payload = {}) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/breach',
      body: payload,
    });
  }

  async getInternalMailList(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/internal-message/list',
    });
  }

  async createInternalMessage(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/internal-message/create',
      body: payload,
    });
  }

  async getProfileInternalMailList(token, profileUid) {
    const uid = String(profileUid || '').trim();
    if (!uid) {
      throw new Error('Profile UID is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/internal-message/profile/${encodeURIComponent(uid)}`,
    });
  }

  async forgotPassword(email) {
    const redirectUrl = `${window.location.origin}/reset-password`;
    return this.client.request('/profile/forgot-password', {
      method: 'POST',
      body: { email, redirectUrl },
    });
  }

  async resetPassword(otp, newPassword) {
    return this.client.request('/profile/reset-password', {
      method: 'POST',
      body: { otp, newPassword },
    });
  }

  async getSupportTickets(token, payload = {}) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/support/ticket',
      body: payload,
    });
  }

  async getSupportTicket(token, uid) {
    const id = String(uid || '').trim();
    if (!id) throw new Error('Ticket uid is required');
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/support/ticket/${encodeURIComponent(id)}`,
    });
  }

  async getSupportTicketComments(token, uid) {
    const id = String(uid || '').trim();
    if (!id) throw new Error('Ticket uid is required');
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/support/ticket/${encodeURIComponent(id)}/comments`,
    });
  }

  async updateSupportTicketStatus(token, uid, status) {
    const id = String(uid || '').trim();
    if (!id) throw new Error('Ticket uid is required');
    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/support/ticket/${encodeURIComponent(id)}/status`,
      body: { status },
    });
  }

  async assignSupportTicket(token, uid, managerId) {
    const id = String(uid || '').trim();
    const mid = String(managerId || '').trim();
    if (!id) throw new Error('Ticket uid is required');
    if (!mid) throw new Error('Manager id is required');
    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/support/ticket/${encodeURIComponent(id)}/assign`,
      body: { managerId: mid },
    });
  }

  async unassignSupportTicket(token, uid) {
    const id = String(uid || '').trim();
    if (!id) throw new Error('Ticket uid is required');
    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/support/ticket/${encodeURIComponent(id)}/unassign`,
    });
  }

  async getMercureToken(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/mercure/token',
    });
  }

  async getNotifications(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/notification',
    });
  }

  async getNotificationCount(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/notification/count',
    });
  }

  async deleteNotification(token, uid) {
    const id = String(uid || '').trim();
    if (!id) throw new Error('Notification uid is required');
    return this.requestWithAuth({
      token,
      method: 'DELETE',
      path: `/admin/notification/${encodeURIComponent(id)}`,
    });
  }

  async deleteAllNotifications(token) {
    return this.requestWithAuth({
      token,
      method: 'DELETE',
      path: '/admin/notification',
    });
  }

  async downloadSettings(token, { password, passwordConfirm }) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/system/save-restore/download',
      body: { password, passwordConfirm },
    });
  }

  async restoreSettings(token, { file, password, strategy, sections }) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('password', password);
    formData.append('strategy', strategy);
    sections.forEach((s) => formData.append('sections[]', s));
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/system/save-restore/restore',
      body: formData,
    });
  }

  async getTenantBackups(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/system/backups',
    });
  }

  async getTenantBackupRuns(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/system/backups/runs',
    });
  }

  async downloadTenantBackup(token, backup) {
    const normalizedBackup = typeof backup === 'string' ? { objectKey: backup } : (backup || {});
    const normalizedBackupSetId = String(normalizedBackup.backupSetId || '').trim();
    const normalizedObjectKey = String(normalizedBackup.objectKey || '').trim();
    if (!normalizedBackupSetId && !normalizedObjectKey) {
      throw new Error('Backup set id or object key is required');
    }

    return this.requestBlobWithAuth({
      token,
      method: 'POST',
      path: '/admin/system/backups/download',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(normalizedBackupSetId ? { backupSetId: normalizedBackupSetId } : { objectKey: normalizedObjectKey }),
    });
  }

  async downloadTenantBackupFiles(token, backup) {
    const normalizedBackup = backup || {};
    const normalizedBackupSetId = String(normalizedBackup.backupSetId || '').trim();
    if (!normalizedBackupSetId) {
      throw new Error('Backup set id is required');
    }

    return this.requestBlobWithAuth({
      token,
      method: 'POST',
      path: '/admin/system/backups/download',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ backupSetId: normalizedBackupSetId, artifact: 'files' }),
    });
  }

  async restoreTenantBackup(token, { objectKey, backupSetId, confirmDbName }) {
    const normalizedBackupSetId = String(backupSetId || '').trim();
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/system/backups/restore',
      body: {
        ...(normalizedBackupSetId ? { backupSetId: normalizedBackupSetId } : { objectKey }),
        confirmDbName,
      },
    });
  }

  async getSecurityAlerts(token, query = {}) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/security/alerts',
      query,
    });
  }

  async getSecurityAlert(token, uid) {
    const normalizedUid = String(uid || '').trim();
    if (!normalizedUid) {
      throw new Error('Security alert uid is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/security/alerts/${encodeURIComponent(normalizedUid)}`,
    });
  }

  async updateSecurityAlertStatus(token, uid, payload) {
    const normalizedUid = String(uid || '').trim();
    if (!normalizedUid) {
      throw new Error('Security alert uid is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/security/alerts/${encodeURIComponent(normalizedUid)}/status`,
      body: payload,
    });
  }

  async getSecurityPolicy(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/security/policy',
    });
  }

  async updateSecurityPolicy(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: '/admin/security/policy',
      body: payload,
    });
  }

  async getSystemEventSummary(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/system/events/summary',
    });
  }

  async getSystemEvents(token, query = {}) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/system/events',
      query,
    });
  }

  async getSystemEvent(token, eventId) {
    const normalizedEventId = String(eventId || '').trim();
    if (!normalizedEventId) {
      throw new Error('System event id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/system/events/${encodeURIComponent(normalizedEventId)}`,
    });
  }

  async createSystemEventSupportBundle(token, eventId) {
    const normalizedEventId = String(eventId || '').trim();
    if (!normalizedEventId) {
      throw new Error('System event id is required');
    }

    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/system/events/${encodeURIComponent(normalizedEventId)}/support-bundle`,
    });
  }

  // ── Partners ──────────────────────────────────────────────

  async getPartners(token, payload = {}) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/partner',
      body: payload,
    });
  }

  async getPartner(token, uid) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/partner/${encodeURIComponent(uid)}`,
    });
  }

  async updatePartner(token, uid, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/partner/${encodeURIComponent(uid)}`,
      body: payload,
    });
  }

  async assignClient(token, partnerUid, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/partner/${encodeURIComponent(partnerUid)}/assign-client`,
      body: payload,
    });
  }

  async getPartnerTree(token, uid) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/partner/${encodeURIComponent(uid)}/tree`,
    });
  }

  async getPartnerCommissions(token, uid, payload = {}) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/partner/${encodeURIComponent(uid)}/commissions`,
      body: payload,
    });
  }

  async recalculatePartnerCommissions(token, uid, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/partner/${encodeURIComponent(uid)}/commission-recalculation`,
      body: payload,
    });
  }

  async getPartnerReferrals(token, uid, payload = {}) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/partner/${encodeURIComponent(uid)}/referrals`,
      body: payload,
    });
  }

  // ── Commissions ───────────────────────────────────────────

  async getCommissions(token, payload = {}) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/commission',
      body: payload,
    });
  }

  async approveCommission(token, uid) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/commission/${encodeURIComponent(uid)}/approve`,
    });
  }

  async cancelCommission(token, uid) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/commission/${encodeURIComponent(uid)}/cancel`,
    });
  }

  // ── Commission Plans ──────────────────────────────────────

  async getCommissionPlans(token, { showAll = false } = {}) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/commission-plan',
      query: showAll ? { showAll: '1' } : undefined,
    });
  }

  async getCommissionPlan(token, uid) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/commission-plan/${encodeURIComponent(uid)}`,
    });
  }

  async createCommissionPlan(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/commission-plan',
      body: payload,
    });
  }

  async updateCommissionPlan(token, uid, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: `/admin/commission-plan/${encodeURIComponent(uid)}`,
      body: payload,
    });
  }

  async recalculateCommissionPlanCommissions(token, uid, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/commission-plan/${encodeURIComponent(uid)}/recalculate-commissions`,
      body: payload,
    });
  }

  // ── Marketing Emails ─────────────────────────────────────

  async getMarketingEmailCampaigns(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/marketing/emails/campaigns',
    });
  }

  async createMarketingEmailCampaign(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/marketing/emails/campaigns',
      body: payload,
    });
  }

  async getMarketingEmailCampaign(token, uid) {
    const normalizedUid = String(uid || '').trim();
    if (!normalizedUid) {
      throw new Error('Marketing email campaign uid is required');
    }

    return this.requestWithAuth({
      token,
      method: 'GET',
      path: `/admin/marketing/emails/campaigns/${encodeURIComponent(normalizedUid)}`,
    });
  }

  async updateMarketingEmailCampaign(token, uid, payload) {
    const normalizedUid = String(uid || '').trim();
    if (!normalizedUid) {
      throw new Error('Marketing email campaign uid is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/marketing/emails/campaigns/${encodeURIComponent(normalizedUid)}`,
      body: payload,
    });
  }

  async previewMarketingEmailCampaign(token, uid, payload) {
    const normalizedUid = String(uid || '').trim();
    if (!normalizedUid) {
      throw new Error('Marketing email campaign uid is required');
    }

    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/marketing/emails/campaigns/${encodeURIComponent(normalizedUid)}/preview`,
      body: payload,
    });
  }

  async sendMarketingEmailCampaign(token, uid) {
    const normalizedUid = String(uid || '').trim();
    if (!normalizedUid) {
      throw new Error('Marketing email campaign uid is required');
    }

    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/marketing/emails/campaigns/${encodeURIComponent(normalizedUid)}/send`,
    });
  }

  async getMarketingEmailProviderConnections(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/marketing/emails/providers/connections',
    });
  }

  async createMarketingEmailProviderConnection(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/marketing/emails/providers/connections',
      body: payload,
    });
  }

  async updateMarketingEmailProviderConnection(token, uid, payload) {
    const normalizedUid = String(uid || '').trim();
    if (!normalizedUid) {
      throw new Error('Marketing email provider connection uid is required');
    }

    return this.requestWithAuth({
      token,
      method: 'PATCH',
      path: `/admin/marketing/emails/providers/connections/${encodeURIComponent(normalizedUid)}`,
      body: payload,
    });
  }

  async testMarketingEmailProviderConnection(token, uid) {
    const normalizedUid = String(uid || '').trim();
    if (!normalizedUid) {
      throw new Error('Marketing email provider connection uid is required');
    }

    return this.requestWithAuth({
      token,
      method: 'POST',
      path: `/admin/marketing/emails/providers/connections/${encodeURIComponent(normalizedUid)}/test`,
    });
  }

  async dryRunMarketingEmailSync(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/marketing/emails/sync/dry-run',
      body: payload,
    });
  }

  async createMarketingEmailSyncJob(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'POST',
      path: '/admin/marketing/emails/sync/jobs',
      body: payload,
    });
  }

  async getMarketingEmailSyncJobs(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/marketing/emails/sync/jobs',
    });
  }

  async getMarketingEmailSettings(token) {
    return this.requestWithAuth({
      token,
      method: 'GET',
      path: '/admin/marketing/emails/settings',
    });
  }

  async updateMarketingEmailSettings(token, payload) {
    return this.requestWithAuth({
      token,
      method: 'PUT',
      path: '/admin/marketing/emails/settings',
      body: payload,
    });
  }

}
